import React from 'react';
import { Tag } from 'lucide-react';
import { getTagBadgeClass } from '../../../utils/propertyTagUtils';

interface PropertyTagsListProps {
  tags?: string[] | null;
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  idPrefix?: string;
  showIcon?: boolean;
}

export const PropertyTagsList: React.FC<PropertyTagsListProps> = ({
  tags,
  maxVisible = 3,
  size = 'xs',
  className = '',
  idPrefix = 'prop-tag',
  showIcon = false,
}) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return null;
  }

  const cleanTags = tags.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  if (cleanTags.length === 0) return null;

  const visibleTags = maxVisible > 0 ? cleanTags.slice(0, maxVisible) : cleanTags;
  const remainingCount = maxVisible > 0 ? cleanTags.length - maxVisible : 0;
  const remainingTagsTooltip = remainingCount > 0 ? cleanTags.slice(maxVisible).join(', ') : '';

  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0.5 gap-1',
    sm: 'text-[10px] px-2 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-1 gap-1.5',
  }[size];

  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
  }[size];

  return (
    <div id={`${idPrefix}-list`} className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {visibleTags.map((tag, idx) => {
        const badgeColor = getTagBadgeClass(tag, idx);
        return (
          <span
            key={`${tag}-${idx}`}
            id={`${idPrefix}-item-${idx}`}
            className={`inline-flex items-center font-bold tracking-tight rounded-md border shadow-2xs whitespace-nowrap transition-transform duration-150 ${sizeClasses} ${badgeColor}`}
          >
            {showIcon && <Tag className={`${iconSizes} shrink-0 opacity-70`} />}
            <span>#{tag.trim()}</span>
          </span>
        );
      })}

      {remainingCount > 0 && (
        <span
          id={`${idPrefix}-more-badge`}
          title={`Más tags: ${remainingTagsTooltip}`}
          className={`inline-flex items-center font-extrabold rounded-md border border-gray-200 bg-gray-50 text-gray-600 shadow-2xs cursor-help ${sizeClasses}`}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
};
